import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { KargoApi } from './KargoApi';
import {
  Project,
  ProjectMode,
  BulkResult,
  ApiError,
  LockActionRequest,
  ModeActionRequest,
  OperationFilters,
  OperationPage,
} from './types';

// The kargo-assistant API service, reached through the Backstage proxy.
const PROXY_PATH = '/kargo-assistant';

export class KargoClient implements KargoApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return `${await this.discoveryApi.getBaseUrl('proxy')}${PROXY_PATH}`;
  }

  private async throwErrorFromResponse(response: Response): Promise<never> {
    let message = response.statusText;
    try {
      const body: ApiError = await response.json();
      message = body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      await this.throwErrorFromResponse(response);
    }
    return (await response.json()) as T;
  }

  private async handleEmptyResponse(response: Response): Promise<void> {
    if (!response.ok) {
      await this.throwErrorFromResponse(response);
    }
  }

  async listProjects(): Promise<Project[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/projects`);
    return this.handleResponse<Project[]>(response);
  }

  async getProject(name: string): Promise<Project> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(name)}`,
    );
    return this.handleResponse<Project>(response);
  }

  async listOperations(filters: OperationFilters = {}): Promise<OperationPage> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (filters.project) params.set('project', filters.project);
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.cursor) params.set('cursor', filters.cursor);
    const query = params.toString();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/operations${query ? `?${query}` : ''}`,
    );
    return this.handleResponse<OperationPage>(response);
  }

  async lockProject(name: string, req?: LockActionRequest): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(name)}/lock?source=manual`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req ?? {}),
      },
    );
    return this.handleEmptyResponse(response);
  }

  async unlockProject(name: string, req?: LockActionRequest): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(name)}/unlock?source=manual`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req ?? {}),
      },
    );
    return this.handleEmptyResponse(response);
  }

  async setMode(
    name: string,
    mode: ProjectMode,
    req?: ModeActionRequest,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(name)}/mode?source=manual`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, ...req }),
      },
    );
    return this.handleEmptyResponse(response);
  }

  async lockAll(req?: LockActionRequest): Promise<BulkResult> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/lock-all?source=manual`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req ?? {}),
      },
    );
    return this.handleResponse<BulkResult>(response);
  }

  async unlockAll(req?: LockActionRequest): Promise<BulkResult> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/unlock-all?source=manual`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req ?? {}),
      },
    );
    return this.handleResponse<BulkResult>(response);
  }
}
